import { useState, createContext, useContext, useEffect } from 'react';

import { t } from 'i18next';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import { isInstanceProcessing, isInstanceRunning } from '../apis/constants';
import { solrApis } from '../apis/solrApis';
import useToast, { TOAST_TYPE } from '../hooks/useToast';
import { mapSolrStatusToInstance } from '../utils/utils';

// Constants
const SOLR_INSTANCE_CONTEXT_ERROR =
  'SolrContext must be used within an Solr Instance Provider';

const SolrContext = createContext();

const SolrInstanceProvider = ({ children }) => {
  const { projectId, instanceId } = useParams();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [solrInstance, setSolrInstance] = useState(null);

  /**
   * Takes the Instance Set and checks the Solr Instance's status if the Instance Set is running.
   * @param {Object} solr - The Instance Set object.
   * @returns Instance object with or without the solr status.
   */
  const getSolrStatus = async (solr = solrInstance) => {
    try {
      if (!isInstanceRunning(solr)) throw Error();
      const { data: solrStatus } = await solrApis.getSolrStatus(
        projectId,
        instanceId
      );

      const solrWithStatus = mapSolrStatusToInstance(solr, solrStatus);

      return solrWithStatus;
    } catch (error) {
      return solr;
    }
  };

  /**
   * Fetches the Instance set and add solr's status to it.
   */
  const getInstance = async () => {
    try {
      const { data: instance } = await solrApis.getInstance(
        projectId,
        instanceId
      );

      const instanceWithSolr = await getSolrStatus(instance);
      setSolrInstance(instanceWithSolr);
    } catch (error) {
      showToast(TOAST_TYPE.ERROR, t('instanceError', { ns: 'errors' }));
    }
  };

  /**
   * Refreshes the Solr instance's status
   */
  const refreshSolrStatus = async () => {
    try {
      const instanceWithSolr = await getSolrStatus();
      setSolrInstance(instanceWithSolr);
    } catch (error) {
      showToast(TOAST_TYPE.ERROR, t('solrError', { ns: 'errors' }));
    }
  };

  /**
   * Used by an interval in the context to periodically fetch and update the instance.
   */
  const silentlyGetInstance = async () => {
    try {
      // First check the Instance Set to get it's status
      const { data: instance } = await solrApis.getInstance(
        projectId,
        instanceId
      );
      // If the Instance Set isn't processing, fetch the Solr Instance's status
      if (!isInstanceProcessing(instance)) {
        const instanceWithSolr = await getSolrStatus(instance);
        setSolrInstance(instanceWithSolr);
      } else {
        setSolrInstance(instance);
      }
    } catch (error) {
      // If there is an error, we still want to continue fetching using the interval
      // so we just return the function to silently fail, and not send a fail toast
      // every 30 seconds.
      return;
    }
  };

  useEffect(() => {
    if (!instanceId) return;
    const initialInstanceFetch = async () => {
      setLoading(true);
      await getInstance();
      setLoading(false);
    };

    initialInstanceFetch();

    // Interval to periodically update the instance.
    const solrInterval = setInterval(silentlyGetInstance, 30000);
    // cleanup when navigating away from a instance-scoped page.
    return () => clearInterval(solrInterval);
  }, [instanceId]);

  return (
    <SolrContext.Provider
      value={{
        instance: solrInstance,
        loading: loading,
        refreshSolrStatus,
        refreshInstance: getInstance,
      }}
    >
      {children}
    </SolrContext.Provider>
  );
};

export const useSolrInstance = () => {
  const context = useContext(SolrContext);
  if (context === undefined) {
    throw new Error(SOLR_INSTANCE_CONTEXT_ERROR);
  }
  return context;
};

SolrInstanceProvider.propTypes = {
  children: PropTypes.node,
};

export default SolrInstanceProvider;
